// FILE: src/components/Members.js
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";

const Members = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // For inline editing
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingId, setSavingId] = useState(null);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const membersCollection = query(
        collection(db, "members"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(membersCollection);
      setMembers(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching members:", err);
      alert("Could not load members.");
    } finally {
      setLoading(false);
    }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Delete this member?")) return;

    try {
      await deleteDoc(doc(db, "members", id));
      fetchMembers();
    } catch (err) {
      console.error(err);
      alert("Could not delete member.");
    }
  };

  // Start editing a member row
  const startEditing = (member) => {
    setEditingId(member.id);
    setEditValues({
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      email: member.email || "",
      cellNumber: member.cellNumber || "",
      homeAddress: member.homeAddress || "",
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingId(null);
    setEditValues({});
  };

  // Save edited member to Firestore
  const saveEditing = async (id) => {
    // basic validation (example: email)
    if (!editValues.firstName.trim() || !editValues.lastName.trim()) {
      alert("Please provide both first name and last name.");
      return;
    }
    setSavingId(id);
    try {
      const updateData = {
        firstName: editValues.firstName.trim(),
        lastName: editValues.lastName.trim(),
        email: editValues.email.trim(),
        cellNumber: editValues.cellNumber.trim(),
        homeAddress: editValues.homeAddress.trim(),
      };
      // remove empty strings? keep them — up to you
      await updateDoc(doc(db, "members", id), updateData);
      await fetchMembers();
      setEditingId(null);
      setEditValues({});
    } catch (err) {
      console.error("Could not update member:", err);
      alert("Could not update member.");
    } finally {
      setSavingId(null);
    }
  };

  const downloadCSV = () => {
    if (!members.length) {
      alert("No members to download.");
      return;
    }

    const headers = [
      "id",
      "firstName",
      "lastName",
      "email",
      "cellNumber",
      "homeAddress",
    ];

    const rows = members.map((m) => ({
      id: m.id,
      firstName: m.firstName || "",
      lastName: m.lastName || "",
      email: m.email || "",
      cellNumber: m.cellNumber || "",
      homeAddress: m.homeAddress || "",
    }));

    const csvContent = [
      headers.join(","),
      ...rows.map((r) =>
        headers
          .map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.download = `members-${ts}.csv`;
    a.click();
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  // 🔍 Filter members by firstName, lastName or email
  const filteredMembers = members.filter((m) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const first = (m.firstName || "").toLowerCase();
    const last = (m.lastName || "").toLowerCase();
    const email = (m.email || "").toLowerCase();
    return (
      first.includes(term) ||
      last.includes(term) ||
      `${first} ${last}`.includes(term) ||
      email.includes(term)
    );
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <h2 className="text-2xl font-bold">Members Management</h2>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 w-full sm:w-auto">
          {/* 🔍 Search bar */}
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <button
            onClick={downloadCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
          >
            Download CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-600">Loading members...</div>
      ) : filteredMembers.length === 0 ? (
        <div className="text-gray-600">
          {members.length === 0
            ? "No members found."
            : "No members match your search."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white shadow-md rounded-lg overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-3 text-left text-sm font-medium">
                  First Name
                </th>
                <th className="border p-3 text-left text-sm font-medium">
                  Last Name
                </th>
                <th className="border p-3 text-left text-sm font-medium">
                  Email
                </th>
                <th className="border p-3 text-left text-sm font-medium">
                  Cell Number
                </th>
                <th className="border p-3 text-left text-sm font-medium">
                  Home Address
                </th>
                <th className="border p-3 text-center text-sm font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member) => {
                const isEditing = editingId === member.id;
                return (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="border p-3 text-sm">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValues.firstName}
                          onChange={(e) =>
                            setEditValues((s) => ({ ...s, firstName: e.target.value }))
                          }
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        member.firstName || "—"
                      )}
                    </td>

                    <td className="border p-3 text-sm">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValues.lastName}
                          onChange={(e) =>
                            setEditValues((s) => ({ ...s, lastName: e.target.value }))
                          }
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        member.lastName || "—"
                      )}
                    </td>

                    <td className="border p-3 text-sm">
                      {isEditing ? (
                        <input
                          type="email"
                          value={editValues.email}
                          onChange={(e) =>
                            setEditValues((s) => ({ ...s, email: e.target.value }))
                          }
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        member.email || "—"
                      )}
                    </td>

                    <td className="border p-3 text-sm">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValues.cellNumber}
                          onChange={(e) =>
                            setEditValues((s) => ({ ...s, cellNumber: e.target.value }))
                          }
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        member.cellNumber || "—"
                      )}
                    </td>

                    <td className="border p-3 text-sm">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValues.homeAddress}
                          onChange={(e) =>
                            setEditValues((s) => ({ ...s, homeAddress: e.target.value }))
                          }
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        member.homeAddress || "—"
                      )}
                    </td>

                    <td className="border p-3 text-center space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => saveEditing(member.id)}
                            disabled={savingId === member.id}
                            className="px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-60"
                          >
                            {savingId === member.id ? "Saving…" : "Save"}
                          </button>
                          <button
                            onClick={cancelEditing}
                            disabled={savingId === member.id}
                            className="px-3 py-1 bg-gray-300 text-gray-800 rounded text-xs hover:bg-gray-200 disabled:opacity-60"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditing(member)}
                            className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteMember(member.id)}
                            className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Members;
